import React, { useState, useRef, useEffect } from "react";
import { Card, CardHeader } from "../Cards";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import { LoadingSpinner } from "../Loading";
import { FaEnvelope, FaArrowLeft } from "react-icons/fa";

const CODE_LENGTH = 6;

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

/**
 * OTPCodeInput - Reusable component for entering 6-digit verification codes
 *
 * Features:
 * - Auto-focus and auto-advance between inputs
 * - Backspace navigation
 * - Paste and SMS autofill of a full code (spaces and dashes ignored)
 * - Loading and error states
 *
 * @param {string} email - Email address to display (where code was sent)
 * @param {function} onVerify - Callback when code is verified/submitted (receives code string)
 * @param {function} onCodeChange - Optional callback when code changes (receives code string)
 * @param {function} onBack - Callback for back button
 * @param {function} onResend - Callback for resend code button
 * @param {boolean} loading - Loading state (disables inputs)
 * @param {string} error - Error message to display
 * @param {boolean} verifying - Verifying state (shows loading spinner)
 * @param {boolean} hideVerifyButton - If true, hide the verify button (for real-time validation)
 */
const OTPCodeInput = ({
  email,
  onVerify,
  onCodeChange,
  onBack,
  onResend,
  loading,
  error,
  verifying,
  hideVerifyButton = false,
}) => {
  const [code, setCode] = useState(() => Array(CODE_LENGTH).fill(""));
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const publish = (next) => {
    setCode(next);
    if (onCodeChange) {
      onCodeChange(next.join(""));
    }
  };

  const focusBox = (next) => {
    const empty = next.findIndex((digit) => digit === "");
    const index = empty === -1 ? CODE_LENGTH - 1 : empty;
    requestAnimationFrame(() => {
      inputRefs.current[index]?.focus();
    });
  };

  const fillFrom = (raw, startIndex) => {
    const digits = digitsOnly(raw).slice(0, CODE_LENGTH);
    if (!digits) return;

    const next =
      digits.length === CODE_LENGTH ? digits.split("") : [...code];
    if (digits.length !== CODE_LENGTH) {
      digits.split("").forEach((digit, offset) => {
        const target = startIndex + offset;
        if (target < CODE_LENGTH) next[target] = digit;
      });
    }
    publish(next);
    focusBox(next);
  };

  const handleChange = (index, value) => {
    const digits = digitsOnly(value);
    if (digits.length > 1) {
      const typedOver =
        digits.length === 2 && code[index] && digits[0] === code[index];
      if (typedOver) {
        const next = [...code];
        next[index] = digits[1];
        publish(next);
        if (index < CODE_LENGTH - 1) {
          inputRefs.current[index + 1]?.focus();
        }
        return;
      }
      fillFrom(digits, digits.length === CODE_LENGTH ? 0 : index);
      return;
    }

    if (value && !digits) return;

    const next = [...code];
    next[index] = digits;
    publish(next);

    if (digits && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (index, e) => {
    const text = e.clipboardData?.getData("text") ?? "";
    if (!digitsOnly(text)) return;
    e.preventDefault();
    fillFrom(text, index);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const codeString = code.join("");
    if (codeString.length === CODE_LENGTH) {
      onVerify(codeString);
    }
  };

  const isCodeComplete = code.every((digit) => digit !== "");

  return (
    <Card padding="p-0" className="w-full min-w-0 overflow-hidden">
      <div className="p-4 sm:p-6 w-full min-w-0">
        <CardHeader
          title="Enter Verification Code"
          subtitle={
            <span className="break-all">
              We sent a 6-digit code to {email}
            </span>
          }
        />

        <div className="w-full max-w-sm min-w-0 space-y-6">
          <div className="p-4 card-inset w-full min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FaEnvelope className="w-5 h-5 text-brand-muted flex-shrink-0" />
              <h3 className="font-display font-bold text-brand-charcoal">
                Check Your Email
              </h3>
            </div>
            <p className="text-sm text-brand-charcoal">
              Enter the 6-digit code we sent to your email address. The code
              will expire in 10 minutes.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 w-full min-w-0">
            <div className="w-full min-w-0">
              <label className="block text-sm font-medium text-brand-charcoal mb-2">
                Verification Code
              </label>
              <div className="grid w-full grid-cols-6 gap-2">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    maxLength={CODE_LENGTH}
                    aria-label={`Digit ${index + 1}`}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={(e) => handlePaste(index, e)}
                    onFocus={(e) => e.target.select()}
                    className="aspect-square w-full min-w-0 px-0 text-center text-xl sm:text-2xl font-score font-semibold border-2 border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:border-brand-gold-ink focus:ring-2 focus:ring-brand-gold-ink outline-none"
                    disabled={verifying || loading}
                  />
                ))}
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 text-center">{error}</p>
              )}
            </div>

            <div className="flex gap-4 w-full">
              <SecondaryButton
                onClick={onBack}
                className={
                  hideVerifyButton
                    ? "w-full !px-3 !py-2 text-sm"
                    : "flex-1 !px-3 !py-2 text-sm"
                }
                disabled={verifying || loading}
              >
                <FaArrowLeft className="w-4 h-4 mr-2" />
                Back
              </SecondaryButton>

              {!hideVerifyButton && (
                <PrimaryButton
                  type="submit"
                  disabled={!isCodeComplete || verifying || loading}
                  className="flex-1"
                >
                  {verifying ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify Code"
                  )}
                </PrimaryButton>
              )}
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={onResend}
                disabled={loading}
                className="text-sm text-brand-gold-ink hover:text-brand-charcoal underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Didn't receive the code? Resend
              </button>
            </div>
          </form>
        </div>
      </div>
    </Card>
  );
};

export default OTPCodeInput;
